// frontend/src/pages/SupplierMyQuotesPage.js  (replacement)
//
// Supplier: every devis I have sent, and the place to answer a buyer's
// counter-offer. Same three actions as before — accept, counter, withdraw —
// rebuilt on the chrome/ink primitives so it matches the rest of the supplier
// space, with alert() replaced by inline state on the devis it refers to.
//
// This page existed but was never routed, so a supplier could watch a
// negotiation arrive and had no way to answer it.

import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import API_URL from '../api/baseURL';
import {
  Page, Panel, Button, Badge, Table, Tr, Td,
  EmptyState, Loading, ErrorNote, Note, inputClass,
} from '../components/ui/Iparc';
import { fmt } from '../theme/iparc';

const STATUS = {
  accepted: { label: 'Accepté', tone: 'ok' },
  rejected: { label: 'Rejeté', tone: 'danger' },
  withdrawn: { label: 'Retiré', tone: 'neutral' },
  submitted: { label: 'En attente', tone: 'warn' },
  client_countered: { label: 'Contre-offre reçue', tone: 'warn' },
  supplier_countered: { label: 'Votre contre-offre', tone: 'neutral' },
};
const statusOf = (s) => STATUS[s] || { label: String(s || '—').replace(/_/g, ' '), tone: 'neutral' };

const fmtDate = (d) => {
  try { return d ? new Date(d).toLocaleDateString('fr-FR') : '—'; } catch { return '—'; }
};

const vehicleOf = (r) => {
  const car = r?.rfq?.car;
  const snap = r?.rfq?.vehicleSnapshot;
  return {
    label: [car?.brand || snap?.make, car?.model || snap?.model].filter(Boolean).join(' ') || 'Véhicule',
    year: car?.year || snap?.year,
    plate: car?.plateNumber,
    // The reference a supplier orders against. vinSnapshot is the fallback for
    // the same reason as vehicleSnapshot: it is what the RFQ remembers once the
    // vehicle has been renamed or deleted, and a quote outlives the car record.
    vin: String(car?.vin || r?.rfq?.vinSnapshot || '').trim().toUpperCase(),
  };
};

// The buyer's standing offer, when there is one.
const lastCounter = (r) => {
  const c = Array.isArray(r?.counters) ? r.counters : [];
  return c.length ? c[c.length - 1] : null;
};

export default function SupplierMyQuotesPage({ token: tokenProp }) {
  const { token: ctxToken } = useAuth();
  const token = tokenProp || ctxToken;

  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [counterPrice, setCounterPrice] = useState({});
  const [actionLoading, setActionLoading] = useState({});
  const [actionError, setActionError] = useState({});
  const [actionOk, setActionOk] = useState({});

  const fetchResponses = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/spare-parts/responses`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setResponses(Array.isArray(res.data) ? res.data : []);
      setError('');
    } catch (err) {
      console.error('Error loading responses:', err);
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) return;
    fetchResponses();
    // eslint-disable-next-line
  }, [token]);

  const handleAction = async (action, id, price) => {
    setActionError((prev) => ({ ...prev, [id]: '' }));
    setActionOk((prev) => ({ ...prev, [id]: '' }));

    if (action === 'counter') {
      const n = Number(price);
      if (!price || Number.isNaN(n) || n <= 0) {
        setActionError((prev) => ({ ...prev, [id]: 'Entrez un prix valide supérieur à 0.' }));
        return;
      }
    }

    setActionLoading((prev) => ({ ...prev, [id]: action }));
    try {
      let url = '';
      let method = 'POST';
      let body = {};

      if (action === 'accept') {
        // Accepting the buyer's counter-offer. The price is theirs — the
        // server ignores any figure a supplier sends here.
        url = `${API_URL}/quotations/accept/${id}`;
        method = 'PUT';
      } else if (action === 'counter') {
        url = `${API_URL}/quotations/responses/${id}/counter`;
        body = { price: Number(price) };
      } else if (action === 'withdraw') {
        url = `${API_URL}/quotations/responses/${id}/reject`;
        body = { note: 'Retiré par le fournisseur' };
      }

      const res = await fetch(url, {
        method,
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: Object.keys(body).length ? JSON.stringify(body) : undefined,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Action échouée');

      setResponses((prev) => prev.map((r) => (r._id === id
        ? { ...r, status: data.status || r.status, counters: data.counters || r.counters }
        : r)));

      if (action === 'counter') setCounterPrice((prev) => ({ ...prev, [id]: '' }));

      setActionOk((prev) => ({
        ...prev,
        [id]: {
          accept: 'Contre-offre acceptée. La facture a été créée.',
          counter: 'Contre-offre envoyée.',
          withdraw: 'Devis retiré.',
        }[action],
      }));
    } catch (err) {
      setActionError((prev) => ({ ...prev, [id]: err.message }));
    } finally {
      setActionLoading((prev) => ({ ...prev, [id]: null }));
    }
  };

  const groups = useMemo(() => ({
    // The buyer has a price on the table and is waiting on the supplier.
    needsAction: responses.filter((r) => r.status === 'client_countered'),
    waiting: responses.filter((r) => ['submitted', 'supplier_countered'].includes(r.status)),
    closed: responses.filter((r) => ['accepted', 'rejected', 'withdrawn'].includes(r.status)),
  }), [responses]);

  const renderQuote = (r, { actionable }) => {
    const st = statusOf(r.status);
    const veh = vehicleOf(r);
    const busy = !!actionLoading[r._id];
    const offer = lastCounter(r);

    return (
      <Panel
        key={r._id}
        title={`${veh.label}${veh.year ? ` · ${veh.year}` : ''}`}
        action={<Badge tone={st.tone}>{st.label}</Badge>}
      >
        <div className="flex flex-wrap items-baseline justify-between gap-3 pb-3.5 border-b border-[#241F1B]">
          <span className="text-[11.5px] text-[#8F877B]">
            {veh.plate && <span className="font-['Space_Grotesk',sans-serif] text-[#C9C3B6]">{veh.plate}</span>}
            {veh.plate && ' · '}
            Envoyé le {fmtDate(r.createdAt)}
            {veh.vin && (
              <>
                <br />
                <span className="text-[#5C554C]">VIN </span>
                <span className="font-['Space_Grotesk',sans-serif] tracking-[.06em] text-[#8F877B] break-all">
                  {veh.vin}
                </span>
              </>
            )}
          </span>
          <span className="text-[11.5px] text-[#8F877B]">
            Votre prix{' '}
            <span className="font-['Space_Grotesk',sans-serif] tabular-nums text-[#F4F1EA]">
              {fmt(r.totalSupplier)} F
            </span>
          </span>
        </div>

        <Table head={['Désignation', 'Référence', 'Qté', 'P.U.', 'Total']} minWidth={560}>
          {(Array.isArray(r.items) ? r.items : []).map((it, i) => {
            const qty = Number(it.quantity) || 0;
            const unit = Number(it.supplierPrice) || 0;
            return (
              <Tr key={i}>
                <Td className="text-[#F4F1EA]">{it.description || '—'}</Td>
                <Td mono>{it.reference || '—'}</Td>
                <Td mono className="text-center">{qty}</Td>
                <Td mono className="text-right">{fmt(unit)}</Td>
                <Td mono className="text-right text-[#F4F1EA]">{fmt(unit * qty)}</Td>
              </Tr>
            );
          })}
        </Table>

        {/* Negotiation history — who moved, to what, when. */}
        {Array.isArray(r.counters) && r.counters.length > 0 && (
          <div className="mt-3.5 pt-3.5 border-t border-[#241F1B]">
            <p className="text-[10px] uppercase tracking-[.16em] text-[#6E665C] mb-2">Négociation</p>
            <div className="flex flex-col gap-1">
              {r.counters.map((c, i) => (
                <div key={i} className="flex items-baseline justify-between gap-4 text-[12px]">
                  <span className="text-[#C9C3B6]">{c.by === 'supplier' ? 'Vous' : 'Le client'}</span>
                  <span className="font-['Space_Grotesk',sans-serif] tabular-nums text-[#F4F1EA]">
                    {fmt(c.price)} F
                  </span>
                  <span className="text-[#5C554C] tabular-nums">{fmtDate(c.at)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {actionError[r._id] && <div className="mt-3.5"><ErrorNote>{actionError[r._id]}</ErrorNote></div>}
        {actionOk[r._id] && <div className="mt-3.5"><Note tone="ok">{actionOk[r._id]}</Note></div>}

        {actionable ? (
          <div className="mt-3.5 pt-3.5 border-t border-[#241F1B]">
            {offer && r.status === 'client_countered' && (
              <p className="text-[12.5px] text-[#C9C3B6] mb-2.5">
                Le client propose{' '}
                <span className="font-['Space_Grotesk',sans-serif] tabular-nums text-[#F4F1EA]">
                  {fmt(offer.price)} F
                </span>{' '}
                au lieu de {fmt(r.totalSupplier)} F.
              </p>
            )}

            <div className="flex flex-wrap gap-2.5 items-center">
              {r.status === 'client_countered' && (
                <Button as="button" type="button" variant="primary"
                  onClick={() => handleAction('accept', r._id)} disabled={busy}>
                  {actionLoading[r._id] === 'accept' ? '…' : `Accepter ${fmt(offer?.price)} F`}
                </Button>
              )}

              <div className="flex items-center gap-2">
                <input
                  type="number" min="1" placeholder="Votre prix (F)"
                  className={`${inputClass} w-[150px] h-[34px] text-right`}
                  value={counterPrice[r._id] || ''}
                  onChange={(e) => setCounterPrice({ ...counterPrice, [r._id]: e.target.value })}
                />
                <Button as="button" type="button"
                  onClick={() => handleAction('counter', r._id, counterPrice[r._id])} disabled={busy}>
                  {actionLoading[r._id] === 'counter' ? '…' : 'Contre-offre'}
                </Button>
              </div>

              <Button as="button" type="button" variant="danger" className="ml-auto"
                onClick={() => handleAction('withdraw', r._id)} disabled={busy}>
                {actionLoading[r._id] === 'withdraw' ? '…' : 'Retirer'}
              </Button>
            </div>
          </div>
        ) : (
          r.status === 'accepted' && (
            <p className="mt-3.5 pt-3.5 border-t border-[#241F1B] text-[12.5px] text-[#74A98D]">
              Devis accepté{r.finalAgreedPrice ? ` à ${fmt(r.finalAgreedPrice)} F` : ''}.
            </p>
          )
        )}
      </Panel>
    );
  };

  return (
    <Page
      subtitle="Pièces détachées"
      title="Mes devis envoyés"
      actions={<Button onClick={fetchResponses} disabled={loading}>Actualiser</Button>}
    >
      {error && <ErrorNote>{error}</ErrorNote>}

      {loading ? (
        <Panel><Loading label="Chargement de vos devis…" /></Panel>
      ) : responses.length === 0 ? (
        <Panel>
          <EmptyState
            title="Aucun devis envoyé"
            hint="Les devis que vous envoyez en réponse aux demandes apparaîtront ici."
          />
        </Panel>
      ) : (
        <>
          {groups.needsAction.length > 0 && (
            <>
              <Note tone="warn">
                {groups.needsAction.length} contre-offre{groups.needsAction.length > 1 ? 's' : ''}{' '}
                {groups.needsAction.length > 1 ? 'attendent' : 'attend'} votre réponse.
              </Note>
              {groups.needsAction.map((r) => renderQuote(r, { actionable: true }))}
            </>
          )}

          {groups.waiting.length > 0 && (
            <>
              <p className="text-[10.5px] uppercase tracking-[.16em] text-[#6E665C] mt-2">
                En attente du client
              </p>
              {/* Still actionable: a supplier may improve their price or
                  withdraw while the buyer is deciding. */}
              {groups.waiting.map((r) => renderQuote(r, { actionable: true }))}
            </>
          )}

          {groups.closed.length > 0 && (
            <>
              <p className="text-[10.5px] uppercase tracking-[.16em] text-[#6E665C] mt-2">
                Terminés
              </p>
              {groups.closed.map((r) => renderQuote(r, { actionable: false }))}
            </>
          )}
        </>
      )}
    </Page>
  );
}
